<template>
  <form novalidate
    @submit.prevent="submit"
    class="event-promotion-form form-spacing"
  >
    <div v-if="id !== null" xs6 class="mb-4">
      <v-select
        v-model="status"
        :items="promotionsStatuses"
        item-title="name"
        item-value="id"
        :label="$t('status')"
        :hide-details="false"
        persistent-hint
        :hint="$t('theming_status_hint')"
        :disabled="!isActivated"
      />
    </div>

    <PromotionEventInformation
      v-if="id !== null"
      :asset="asset"
      :promotion-type="promotionType"
      :core-event="coreEvent"
      :country="country"
    />

    <layout-section-header
      :title="$t('banner_details')"
      :title-right-text="$t('step_2_of_2')"
      :has-trigger="false"
    />

    <magic-container class="mb-4">
      <div>
        <div class="d-flex align-center justify-space-between mb-6">
          {{ $t('show_banner_countdown') }}

          <v-switch
            v-model="isCountdown"
            inset
            class="mt-0"
            hide-details
          />
        </div>

          <transition name="slide-down-autocomplete">
            <base-date-picker
              v-if="isCountdown"
              placeholder="Countdown end date"
              :date="countdownEndDateTime"
              is-required
              :config="dateTimeConfig"
              :error-messages="countdownEndDateTimeErrors"
              :on-change="onChangeCountdownEndDateTime"
              :on-blur="v$.countdownEndDateTime.$touch"
              :hide-details="false"
              :hint="`(${activePromotion?.country.code} time)`"
              persistent-hint
            />
          </transition>

        <v-text-field
          v-model="title"
          :label="$t('banner_title')"
          :error-messages="titleErrors"
          :hint="$t('event_title_hint')"
          :hide-details="false"
          persistent-hint
          :counter="30"
          :maxlength="30"
        />

        <v-text-field
          v-model="subtitle"
          :label="$t('banner_subtitle')"
          :error-messages="subtitleErrors"
          :hint="$t('banner_subtitle_hint')"
          :hide-details="false"
          :counter="78"
          :maxlength="78"
          persistent-hint
        />

        <v-autocomplete
          v-model="selectedSponsorships"
          multiple
          return-object
          :items="sponsorships"
          :label="$t('sponsorship')"
          type="text"
          item-title="name"
          item-value="id"
          :error-messages="selectedSponsorshipsErrors"
          v-model:search="sponsorshipsSearchInput"
          @update:search="onSponsorshipSearch"
          @update:model-value="onSelectSponsorship()"
          :hide-details="false"
          persistent-hint
          :hint="$t('new_event_sponsorship_hint')"
          @blur="v$.selectedSponsorships.$touch()"
          :counter="5"
        />
      </div>

      <div xs6>
        <div :class="`event-promotion-form__preview${activePromotion.asset.color.id === 2 ? '--black' : ''}`">
          <img
            :src="activePromotion.asset.images[0]?.url"
            alt="Event Preview"
            class="event-promotion-form__preview-image"
          >

          <transition name="scale-down">
            <div v-if="isCountdown" class="event-promotion-form__preview-countdown">
              <v-icon size="small" :color="activePromotion.asset.color.id === 2 ? 'black' : 'white'" class="material-icons-outlined mr-1">
                schedule
              </v-icon>
              Ends {{ getCountdownText() }}
            </div>
          </transition>

          <div class="event-promotion-form__preview-title">
            {{ title?.length > 0 ? title : 'Banner title' }}
          </div>

          <div class="event-promotion-form__preview-subtitle">
            {{ subtitle?.length > 0 ? subtitle : 'Banner subtitle' }}
          </div>

          <v-btn
            class="event-promotion-form__preview-button mt-4"
            :color="activePromotion.asset.color.id === 2 ? 'white' : 'primary'"
            dense
          >
            View all deals
            <v-icon
              size="small"
              :color="activePromotion.asset.color.id === 2 ? 'black' : 'white'"
              class="material-icons-outlined ml-1 mr-0"
            >
              arrow_forward
            </v-icon>
          </v-btn>

          <div v-if="selectedSponsorships.length > 0" class="event-promotion-form__preview-merchants">
            <div class="event-promotion-form__preview-info flex-wrap">
              <template v-for="sponsorship in selectedSponsorships" :key="sponsorship.id">
                <img
                  :src="sponsorship.sponsorship_logo_url"
                  :alt="sponsorship.name"
                  class="event-promotion-form__preview-merchants-logo mr-2"
                >
              </template>
              <span class="d-flex align-center gap-4">
                Sponsored
                <v-icon size="small" :color="activePromotion.asset.color.id === 2 ? 'black' : 'white'" class="material-icons-outlined">
                  info
                </v-icon>
              </span>
            </div>
          </div>

          <div v-else class="event-promotion-form__preview-merchants__empty">
            No sponsors added
          </div>
        </div>

        <div class="d-flex align-center justify-space-between mt-6">
          <div class="mb-0">
            Deal Carrousel
            <div class="text-caption">
              Show the hottest deals related with the event
            </div>
          </div>

          <v-switch
            v-model="isDealCarrousel"
            inset
            class="mt-0"
            hide-details
          />
        </div>
      </div>
    </magic-container>

    <layout-section-header
      :title="$t('banner_duration')"
      :has-trigger="false"
      class="mt-8"
    />

    <magic-container>
      <base-date-picker
        placeholder="Banner start date"
        :date="bannerStartDate"
        is-required
        :config="dateConfig"
        :error-messages="bannerStartDateErrors"
        :on-change="onChangeBannerStartDate"
        :on-blur="v$.bannerStartDate.$touch"
        :hide-details="false"
        persistent-hint
        :hint="$t('when_the_banner_will_go_live')"
        :disabled="id !== null && isActivated"
        :has-clear="false"
      />

      <base-date-picker
        placeholder="Banner end date"
        :date="bannerEndDate"
        is-required
        :config="endDateConfig"
        :error-messages="bannerEndDateErrors"
        :on-change="onChangeBannerEndDate"
        :on-blur="v$.bannerEndDate.$touch"
        :hide-details="false"
        persistent-hint
        :hint="$t('when_the_banner_will_end')"
        :has-clear="false"
      />
    </magic-container>
    <magic-container>
      <time-select
        :time="bannerStartTime"
        required
        :on-change="(v) => bannerStartTime = v"
        label="Banner start time"
        :error-messages="bannerStartTimeErrors"
        :on-blur="v$.bannerStartTime.$touch"
        :attach="false"
        :hide-details="false"
        :disabled="isActivated"
        persistent-hint
        hint="(CET time)"
      />

      <time-select
        :time="bannerEndTime"
        required
        :on-change="(v) => bannerEndTime = v"
        label="Banner end time"
        :error-messages="bannerEndTimeErrors"
        :on-blur="v$.bannerEndTime.$touch"
        :attach="false"
        :hide-details="false"
        persistent-hint
        hint="(CET time)"
      />
    </magic-container>

    <div class="d-flex justify-space-between mt-4">
      <v-btn
        v-if="id === null"
        variant="outlined"
        @click="back"
        color="primary"
      >
        {{ $t('back') }}
      </v-btn>

      <div class="form-buttons">
        <v-btn
          variant="text"
          @click="cancel"
        >
          {{ $t('cancel') }}
        </v-btn>

        <v-btn
          color="primary"
          class="trigger"
          variant="flat"
          type="submit"
          :loading="isLoading"
        >
          {{ id ? $t('update_event_banner') : $t('create_event_banner') }}
        </v-btn>
      </div>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core'
import { required, maxLength } from '@vuelidate/validators'
import { debounce, isObj } from '@/utils';
import fetchIfNotInStoreMixin from '@/mixins/fetchIfNotInStore';
import { v1 as uuidv1 } from 'uuid';
import PromotionEventInformation from './PromotionEventInformation.vue';

const sponsorshipsSearchDebouncer = debounce({
  id: uuidv1(),
  timeout: 500
});

export default {
  emits: ['saved'],
  mixins: [fetchIfNotInStoreMixin],
  components: {
    PromotionEventInformation,
  },
  data() {
    return {
      isLoading: false,
      isCountdown: false,
      isDealCarrousel: false,
      countdownEndDateTime: '',
      title: '',
      subtitle: '',
      selectedSponsorships: [],
      sponsorshipsSearchInput: '',
      isSponsorshipsLoading: false,
      dateTimeConfig: {
        format: 'm/d/Y H:i',
        altFormat: 'm/d/Y H:i',
        time_24hr: true,
        enableTime: true,
        minDate: 'today',
      },
      dateConfig: {
        shorthandCurrentMonth: true,
        minDate: 'today',
      },
      endDateConfig: {
        minDate: this.getMinEndDate(),
      },
      bannerStartDate: '',
      bannerStartTime: this.getStartTime(),
      bannerEndDate: '',
      bannerEndTime: '23:45',
      status: null,
      id: null,
      country: null,
      coreEvent: null,
      asset: null,
      promotionType: null,
      isActivated: false,
    };
  },
  setup () {
    return { v$: useVuelidate() }
  },
  validations() {
    return {
      countdownEndDateTime: { required: v => !this.isCountdown || v },
      title: { required },
      subtitle: { required },
      selectedSponsorships: { maxLength: maxLength(5) },
      bannerStartDate: { required },
      bannerStartTime: { required },
      bannerEndDate: { required },
      bannerEndTime: { required },
    };
  },
  computed: {
    ...mapGetters('events', ['activePromotion', 'promotionsStatuses']),
    sponsorships() {
      const merchants = this.$store.getters['pepper/merchants'];
      const merchantsWithoutSelectedOnes = merchants.filter(merchant => !this.selectedSponsorships.find(sponsorship => sponsorship.id === merchant.id));
      return [...this.selectedSponsorships, ...merchantsWithoutSelectedOnes];
    },
    countdownEndDateTimeErrors() {
      if (!this.v$.countdownEndDateTime.$dirty) return [];
      if (this.$dayjs(this.countdownEndDateTime).isBefore(this.$dayjs())) {
        return [this.$t('date_time_must_be_future')];
      }
      return this.v$.countdownEndDateTime.$errors.map(() => this.$t('required'));
    },
    titleErrors() {
      return this.v$.title.$errors.map(error => {
        if (error.$validator === 'required') {
          return this.$t('required');
        }
        return error.$message;
      });
    },
    subtitleErrors() {
      return this.v$.subtitle.$errors.map(error => {
        if (error.$validator === 'required') {
          return this.$t('required');
        }
        return error.$message;
      });
    },
    selectedSponsorshipsErrors() {
      const errors = [];
      if (!this.v$.selectedSponsorships.$dirty) return errors;
      this.selectedSponsorships.length > 5 && errors.push(this.$t('max_5_sponsors'));
      return errors;
    },
    bannerStartDateErrors() {
      return this.v$.bannerStartDate.$errors.map(() => this.$t('required'));
    },
    bannerEndDateErrors() {
      return this.v$.bannerEndDate.$errors.map(() => this.$t('required'));
    },
    bannerStartTimeErrors() {
      return this.v$.bannerStartTime.$errors.map(() => this.$t('required'));
    },
    bannerEndTimeErrors() {
      return this.v$.bannerEndTime.$errors.map(() => this.$t('required'));
    },
  },
  created() {
    if (isObj(this.activePromotion) && !this.$route.params.id) {
      this.getSponsorships();
    }
  },
  methods: {
    getMinEndDate() {
      return this.$dayjs().subtract(1, 'day').format('YYYY-MM-DD');
    },
    onSelectSponsorship() {
      if (this.sponsorshipsSearchInput?.length > 0) {
        this.sponsorshipsSearchInput = ''; // clear search input
      }
      this.getSponsorships();// refresh sponsorships
    },
    back() {
      // `step`, not `status`: this is the list's query, and its status filter owns that key.
      this.$router.push({ name: 'event-promotion-new', query: { ...this.$route.query, step: 'back' } });
    },
    getSponsorships(search = '') {
      this.isSponsorshipsLoading = true;
      if (search?.length !== 0 && search?.length < 2) {
        this.$store.dispatch('alerts/set', {
          data: {
            message: 'Search must be at least 2 characters long.',
          },
          alertType: 'error',
        });
        return;
      }

      this.$store.dispatch('pepper/getMerchants', {
        countries: [this.activePromotion.country.code],
        has_logo: true,
        search: search?.length ? search : null,
      }).finally(() => {
        this.isSponsorshipsLoading = false;
      });
    },
    onSponsorshipSearch(search) {
      sponsorshipsSearchDebouncer(() => this.getSponsorships(search));
    },
    getStartTime() {
      return this.$dayjs()
        .add(30 - (this.$dayjs().minute() % 30), 'minutes')
        .format('HH:mm');
    },
    getCountdownText() {
      // fallback if date time does not exist or if it is in the past
      if (!this.countdownEndDateTime || this.$dayjs(this.countdownEndDateTime).isBefore(this.$dayjs())) {
        return 'in ...';
      }

      // check if it's today
      if (this.$dayjs(this.countdownEndDateTime).isSame(this.$dayjs(), 'day')) {
        const endDateTime = this.$dayjs(this.countdownEndDateTime).add(1, 'minute'); // add 1 minute is to round up the time
        // Calculate the duration between the target time and now
        const duration = this.$dayjs.duration(this.$dayjs().diff(endDateTime));
        const totalMinutes = Math.abs(duration.asMinutes());
        const hours = Math.floor(totalMinutes / 60);
        const minutes = Math.floor(totalMinutes % 60);

        return 'in ' +hours + 'h ' + minutes + 'm';
      }

      // if it's not today, let's rely on $dayjs
      return this.$dayjs(this.countdownEndDateTime).fromNow();
    },
    cancel() {
      const { step, ...listQuery } = this.$route.query;
      this.$router.push({ name: 'event-promotions', query: listQuery });
    },
    async submit() {
      if (this.isLoading) return;

      this.isLoading = true;
      const isValid = await this.v$.$validate();
      if (!isValid) {
        this.isLoading = false;
        return;
      }

      const action = this.id !== null ? 'updatePromotion' : 'createPromotion';

      const payload = {
        // fields from first step
        country: this.activePromotion.country.code,
        // Null once the event or asset behind the banner is deleted; the backend says what is missing.
        core_event_id: this.activePromotion.event?.id,
        event_asset_id: this.activePromotion.asset?.id,
        type: 2, // 1 = theming, 2 = banner

        // fields from second step
        show_carousel: this.isDealCarrousel ? 1 : 0, // needed for banner only
        title: this.title,
        subtitle: this.subtitle,
        start_date: this.bannerStartDate + ' ' + this.bannerStartTime,
        end_date: this.bannerEndDate + ' ' + this.bannerEndTime,
      };

      if (this.selectedSponsorships.length !== 0) {
        payload.sponsorships = [{
          merchants: this.selectedSponsorships.map(merchant => merchant.id),
        }];
      } else {
        payload.sponsorships = null;
      }

      if (this.isCountdown) {
        payload.countdown_date = this.countdownEndDateTime;
      } else {
        payload.countdown_date = null;
      }

      if (this.id !== null) {
        payload.id = this.id;
        payload.status = this.status;
      }

      this.$store.dispatch(`events/${action}`, payload).then(() => {
        this.$emit('saved');
        const { step, ...listQuery } = this.$route.query;
        this.$router.push({ name: 'event-promotions', query: listQuery });
      })
        .catch((err) => err)
        .finally(() => {
          this.isLoading = false;
        });
    },
    onChangeCountdownEndDateTime(date) {
      this.countdownEndDateTime = date;
    },
    onChangeBannerStartDate(date) {
      this.bannerStartDate = date;
    },
    onChangeBannerEndDate(date) {
      this.bannerEndDate = date;
    },
    onReceiveItem(data) {
      this.id = data.id;
      this.country = data.country;
      this.status = data.status.id;
      this.coreEvent = data.event;
      this.asset = data.asset;
      this.promotionType = data.type;

      this.isActivated = data.activated?.id === 1;
      this.countdownEndDateTime = data.countdown_date === null ? '' : data.countdown_date;
      this.isCountdown = data.countdown_date !== null;
      this.isDealCarrousel = data.show_carousel === 1;
      this.title = data.title;
      this.subtitle = data.subtitle;
      if (data.sponsorships[0]?.merchants?.length) {
        this.selectedSponsorships = data.sponsorships[0].merchants.map(merchant => ({
          ...merchant,
          id: merchant.merchant_id,
        }));
      }
      this.bannerStartDate = data.start_date.split(' ')[0];
      this.bannerStartTime = data.start_date.split(' ')[1].trim();
      this.bannerEndDate = data.end_date.split(' ')[0];
      this.bannerEndTime = data.end_date.split(' ')[1].trim();

      this.getSponsorships();
      this.getPromotionsStatuses();
    },
    getPromotionsStatuses() {
      this.$store.dispatch('events/getPromotionsStatuses', {
        country: this.country.code,
      });
    },
  },
  watch: {
    '$route.params.id': {
      handler(id) {
        if (id) {
          this.fetchIfNotInStore(id,'events', 'promotions', 'getPromotion')
            .then((data) => {
              // this.clear();
              this.onReceiveItem(data);
            }, () => this.leaveMissingItem(id));
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style lang="scss">

</style>
